import {
  Box, Grid, Button, Typography, TextField,
} from '@material-ui/core';
import * as React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';

import { Autocomplete } from '@material-ui/lab';
import RepairResults from './LocationRepairResults';
import type { IRepairResult } from './RepairLookupInterfaces';
import globalUseStyles from '../../globalClasses';
import getDepartments from '../../lib/SWRfetchers/getDepartments';
import type ILabelEnumPair from '../global/GlobalInterfaces';

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
}));

interface ILocationRepairs {
  location: string;
  repairs: IRepairResult[];
}

const LocationRepairs: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const globalStyles = globalUseStyles();

  const { departments } = getDepartments();

  const [resultSet, setResultSet] = React.useState<ILocationRepairs[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentDept, setCurrentDept] = React.useState<ILabelEnumPair>();

  const handleRefineResults = (selectedDepartment: string) => {
    setLoading(true);
    setCurrentDept({ value: 0, label: selectedDepartment || '' });
    axios.get<ILocationRepairs[]>(`/repairLookup/LocationRepairs?department=${selectedDepartment || ''}`)
      .then((response) => {
        setResultSet(response.data);
        setLoading(false);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        setLoading(false);
      });
  };

  const exportStationToCsvFile = (sheetData: IRepairResult[], sheetName: string) => {
    // clean up data to clean column names
    const cleanedData = sheetData.map((row) => {
      const cleanedRow = {
        Barcode: row.barcode,
        ShipLog: row.shipLog,
        'Store Repair Number': row.storeRepairNumber,
        Facility: row.facility,
        Company: row.company,
        Store: row.store,
        Station: row.station,
        Location: row.location,
        'Last Update': row.lastModified,
        'Assigned To': row.assignedTo,
      };
      return cleanedRow;
    });
    /* convert state to workbook */
    const ws = XLSX.utils.json_to_sheet(cleanedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws);
    /* generate XLSX file and send to client */
    XLSX.writeFile(wb, `${sheetName}.csv`);
  };

  const exportAllStationsToXlxsFile = (sheetData: ILocationRepairs[], sheetName: string) => {
    // clean up data to clean column names
    const wb = XLSX.utils.book_new();
    sheetData.forEach(
      (station) => {
        const cleanedData = station.repairs.map((row) => {
          const cleanedRow = {
            Barcode: row.barcode,
            ShipLog: row.shipLog,
            'Store Repair Number': row.storeRepairNumber,
            Facility: row.facility,
            Company: row.company,
            Store: row.store,
            Station: row.station,
            Location: row.location,
            'Last Update': row.lastModified,
            'Assigned To': row.assignedTo,
          };
          return cleanedRow;
        });
        /* convert state to workbook */
        const ws = XLSX.utils.json_to_sheet(cleanedData);
        XLSX.utils.book_append_sheet(wb, ws, station.location);
      },
    );
    /* generate XLSX file and send to client */
    XLSX.writeFile(wb, `${sheetName}.xlsx`);
  };

  const LocationMap = (
    resultSet.map((filter: ILocationRepairs) => (
      <Grid item xs={12}>
        <Box pt={2} pb={2}>
          <RepairResults
            loading={loading}
            resultSet={filter.repairs || []}
            gridTitle={`${filter.location} - ${filter.repairs.length} Repair${filter.repairs.length > 1 ? 's' : ''}`}
          />
        </Box>
        <Button variant="contained" color="inherit" className={globalStyles.blueContainedButton} onClick={() => { exportStationToCsvFile(filter.repairs, filter.location); }}>
          {`Export ${filter.location} data table to csv`}
        </Button>
      </Grid>
    )));

  React.useEffect(() => {
    handleRefineResults('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} container spacing={3}>
          <Grid item xs={2}>
            <Button variant="contained" color="primary" disabled={loading} onClick={() => handleRefineResults('')}>{'Refresh'}</Button>
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={4}>
            {
              departments ? (
                <Box pl={2}>
                  <Autocomplete
                    value={currentDept}
                    onChange={(e, selectedDepartment: ILabelEnumPair | null) => {
                      if (selectedDepartment) {
                        setCurrentDept(selectedDepartment);
                        handleRefineResults(selectedDepartment.label);
                      } else {
                        handleRefineResults('');
                      }
                    }}
                    autoHighlight
                    autoSelect
                    options={departments.sort((a, b) => a.label.localeCompare(b.label))}
                    getOptionLabel={(option) => option.label.toString()}
                    disabled={loading}
                    // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField {...params} label="Search Repairs by Department" id="txt-select-department" variant="outlined" />}
                  />
                </Box>
              ) : (
                <Typography variant="h6">
                  {`Department: ${currentDept?.label || 'All'}`}
                </Typography>
              )
            }
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || resultSet?.length < 1}
              onClick={() => exportAllStationsToXlxsFile(resultSet, 'RepairExport')}
            >
              {'Export All Data to XLXS'}

            </Button>
          </Grid>
          {LocationMap}
        </Grid>
      </Grid>
    </Box>
  );
};
export default LocationRepairs;
